import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdminBookingResponseDto } from '@pro-sto/api-client';
import {
  AUTO_SCROLL_EDGE_PX,
  AUTO_SCROLL_MAX_STEP,
  CARD_PADDING,
  DRAG_THRESHOLD_PX,
  MIN_DURATION_MINUTES,
  PIXELS_PER_HOUR,
  PIXELS_PER_MINUTE,
  PIXELS_PER_SLOT,
  POST_COLUMN_GAP,
  clamp,
  getMinBookableStartMinutes,
  getPostIndexFromX,
  getWorkingMinutesBounds,
  isStartInPast,
  isWithinWorkingHours,
  minutesFromMidnight,
  rangesOverlap,
  setMinutesFromMidnight,
  snapMinutes,
  type CalendarWorkingHours,
} from './calendarGeometry';

export type CalendarDragMode = 'move' | 'resize';
export type CalendarResizeEdge = 'start' | 'end';

type InvalidPlacementReason = 'occupied' | 'working_hours' | 'past';

const INVALID_PLACEMENT_MESSAGES: Record<InvalidPlacementReason, string> = {
  occupied: 'Это время уже занято',
  working_hours: 'Запись выходит за рабочие часы',
  past: 'Нельзя перенести запись в прошлое',
};

export interface CalendarDragPreview {
  uuid: string;
  mode: CalendarDragMode;
  top: number;
  left: number;
  height: number;
  postIndex: number;
  startMinutes: number;
  endMinutes: number;
  isInvalid: boolean;
}

export interface CalendarReschedulePayload {
  uuid: string;
  start_time: string;
  end_time: string;
  post_uuid?: string;
}

interface UseCalendarDragParams {
  enabled: boolean;
  bookings: AdminBookingResponseDto[];
  minHour: number;
  workingHours: CalendarWorkingHours;
  postColumnWidth: number;
  posts: Array<{ uuid: string }>;
  getPostIndex: (booking: AdminBookingResponseDto) => number;
  containerRef: React.RefObject<HTMLElement | null>;
  scrollContainerRef: React.RefObject<HTMLElement | null>;
  currentDate: Date | undefined;
  onReschedule: (payload: CalendarReschedulePayload) => Promise<boolean>;
  onInvalidDrop?: (message: string) => void;
}

const getScrollOffsets = (scrollContainer: HTMLElement | null) => ({
  top: scrollContainer?.scrollTop ?? 0,
  left: scrollContainer?.scrollLeft ?? 0,
});

const applyAutoScroll = (
  scrollContainer: HTMLElement | null,
  clientX: number,
  clientY: number,
): boolean => {
  if (!scrollContainer) return false;

  const rect = scrollContainer.getBoundingClientRect();
  let scrollTop = 0;
  let scrollLeft = 0;

  const topDistance = clientY - rect.top;
  if (topDistance < AUTO_SCROLL_EDGE_PX) {
    const intensity = 1 - Math.max(0, topDistance) / AUTO_SCROLL_EDGE_PX;
    scrollTop = -Math.ceil(AUTO_SCROLL_MAX_STEP * intensity);
  } else {
    const bottomDistance = rect.bottom - clientY;
    if (bottomDistance < AUTO_SCROLL_EDGE_PX) {
      const intensity = 1 - Math.max(0, bottomDistance) / AUTO_SCROLL_EDGE_PX;
      scrollTop = Math.ceil(AUTO_SCROLL_MAX_STEP * intensity);
    }
  }

  const leftDistance = clientX - rect.left;
  if (leftDistance < AUTO_SCROLL_EDGE_PX) {
    const intensity = 1 - Math.max(0, leftDistance) / AUTO_SCROLL_EDGE_PX;
    scrollLeft = -Math.ceil(AUTO_SCROLL_MAX_STEP * intensity);
  } else {
    const rightDistance = rect.right - clientX;
    if (rightDistance < AUTO_SCROLL_EDGE_PX) {
      const intensity = 1 - Math.max(0, rightDistance) / AUTO_SCROLL_EDGE_PX;
      scrollLeft = Math.ceil(AUTO_SCROLL_MAX_STEP * intensity);
    }
  }

  if (scrollTop !== 0 || scrollLeft !== 0) {
    scrollContainer.scrollBy({ top: scrollTop, left: scrollLeft, behavior: 'auto' });
    return true;
  }

  return isNearScrollEdge(scrollContainer, clientX, clientY);
};

const isNearScrollEdge = (
  scrollContainer: HTMLElement,
  clientX: number,
  clientY: number,
): boolean => {
  const rect = scrollContainer.getBoundingClientRect();
  const canScrollDown = scrollContainer.scrollTop + scrollContainer.clientHeight < scrollContainer.scrollHeight;
  const canScrollUp = scrollContainer.scrollTop > 0;
  const canScrollRight = scrollContainer.scrollLeft + scrollContainer.clientWidth < scrollContainer.scrollWidth;
  const canScrollLeft = scrollContainer.scrollLeft > 0;

  const nearTop = clientY - rect.top < AUTO_SCROLL_EDGE_PX && canScrollUp;
  const nearBottom = rect.bottom - clientY < AUTO_SCROLL_EDGE_PX && canScrollDown;
  const nearLeft = clientX - rect.left < AUTO_SCROLL_EDGE_PX && canScrollLeft;
  const nearRight = rect.right - clientX < AUTO_SCROLL_EDGE_PX && canScrollRight;

  return nearTop || nearBottom || nearLeft || nearRight;
};

export const useCalendarDrag = ({
  enabled,
  bookings,
  minHour,
  workingHours,
  postColumnWidth,
  posts,
  getPostIndex,
  containerRef,
  scrollContainerRef,
  currentDate,
  onReschedule,
  onInvalidDrop,
}: UseCalendarDragParams) => {
  const [preview, setPreview] = useState<CalendarDragPreview | null>(null);
  const didDragRef = useRef(false);
  const suppressClickRef = useRef(false);
  const previewRef = useRef<CalendarDragPreview | null>(null);
  const lastPointerRef = useRef({ x: 0, y: 0 });
  const autoScrollFrameRef = useRef<number | null>(null);
  const sessionRef = useRef<{
    mode: CalendarDragMode;
    resizeEdge: CalendarResizeEdge;
    uuid: string;
    pointerId: number;
    startX: number;
    startY: number;
    originLeft: number;
    originStartMinutes: number;
    originEndMinutes: number;
    originPostIndex: number;
    originScrollTop: number;
    originScrollLeft: number;
  } | null>(null);

  const getPlacementIssue = useCallback(
    (
      uuid: string,
      postIndex: number,
      startMinutes: number,
      endMinutes: number,
      mode: CalendarDragMode = 'move',
      resizeEdge: CalendarResizeEdge = 'end',
    ): InvalidPlacementReason | null => {
      if (endMinutes - startMinutes < MIN_DURATION_MINUTES) return 'working_hours';
      if (!isWithinWorkingHours(startMinutes, endMinutes, workingHours)) return 'working_hours';
      if (
        (mode === 'move' || (mode === 'resize' && resizeEdge === 'start')) &&
        isStartInPast(currentDate, startMinutes)
      ) {
        return 'past';
      }

      const hasOverlap = bookings.some((booking) => {
        if (booking.uuid === uuid) return false;
        if (booking.status === 'cancelled') return false;
        if (getPostIndex(booking) !== postIndex) return false;
        const otherStart = minutesFromMidnight(new Date(booking.start_time));
        const otherEnd = minutesFromMidnight(new Date(booking.end_time));
        return rangesOverlap(startMinutes, endMinutes, otherStart, otherEnd);
      });

      return hasOverlap ? 'occupied' : null;
    },
    [bookings, currentDate, getPostIndex, workingHours],
  );

  const updatePreviewFromPointer = useCallback(
    (clientX: number, clientY: number) => {
      const session = sessionRef.current;
      const container = containerRef.current;
      const scrollContainer = scrollContainerRef.current;
      if (!session || !container) return;

      lastPointerRef.current = { x: clientX, y: clientY };

      const { top: scrollTop, left: scrollLeft } = getScrollOffsets(scrollContainer);
      const scrollDeltaY = scrollTop - session.originScrollTop;
      const scrollDeltaX = scrollLeft - session.originScrollLeft;
      const deltaX = clientX - session.startX + scrollDeltaX;
      const deltaY = clientY - session.startY + scrollDeltaY;

      if (
        !didDragRef.current &&
        Math.abs(deltaX) < DRAG_THRESHOLD_PX &&
        Math.abs(deltaY) < DRAG_THRESHOLD_PX
      ) {
        return;
      }
      didDragRef.current = true;

      const gridHeight = Math.max(
        (workingHours.end - minHour + 1) * PIXELS_PER_HOUR,
        container.offsetHeight,
      );

      let nextStart = session.originStartMinutes;
      let nextEnd = session.originEndMinutes;
      let nextPostIndex = session.originPostIndex;

      const { startMinutes: workStartMinutes, endMinutes: workEndMinutes } =
        getWorkingMinutesBounds(workingHours);

      const minBookable = currentDate
        ? getMinBookableStartMinutes(currentDate, workStartMinutes)
        : workStartMinutes;
      const minStart = minBookable ?? workStartMinutes;

      if (session.mode === 'move') {
        const deltaMinutes = snapMinutes(deltaY / PIXELS_PER_MINUTE);
        const duration = session.originEndMinutes - session.originStartMinutes;
        const maxStart = Math.max(minStart, workEndMinutes - duration);
        nextStart = clamp(
          session.originStartMinutes + deltaMinutes,
          minStart,
          maxStart,
        );
        nextEnd = nextStart + duration;
        nextPostIndex = getPostIndexFromX(
          session.originLeft + deltaX + postColumnWidth / 2,
          postColumnWidth,
          posts.length,
        );
      } else if (session.resizeEdge === 'start') {
        const deltaMinutes = snapMinutes(deltaY / PIXELS_PER_MINUTE);
        nextStart = clamp(
          session.originStartMinutes + deltaMinutes,
          minStart,
          session.originEndMinutes - MIN_DURATION_MINUTES,
        );
        nextEnd = session.originEndMinutes;
      } else {
        const deltaMinutes = snapMinutes(deltaY / PIXELS_PER_MINUTE);
        nextEnd = clamp(
          session.originEndMinutes + deltaMinutes,
          session.originStartMinutes + MIN_DURATION_MINUTES,
          workEndMinutes,
        );
      }

      const top = clamp(
        (nextStart - minHour * 60) * PIXELS_PER_MINUTE + CARD_PADDING,
        CARD_PADDING,
        gridHeight - PIXELS_PER_SLOT,
      );
      const height = Math.max(
        PIXELS_PER_SLOT - CARD_PADDING * 2,
        (nextEnd - nextStart) * PIXELS_PER_MINUTE - CARD_PADDING * 2,
      );
      const left = nextPostIndex * (postColumnWidth + POST_COLUMN_GAP);

      const nextPreview: CalendarDragPreview = {
        uuid: session.uuid,
        mode: session.mode,
        top,
        left,
        height,
        postIndex: nextPostIndex,
        startMinutes: nextStart,
        endMinutes: nextEnd,
        isInvalid:
          getPlacementIssue(
            session.uuid,
            nextPostIndex,
            nextStart,
            nextEnd,
            session.mode,
            session.resizeEdge,
          ) !== null,
      };

      previewRef.current = nextPreview;
      setPreview(nextPreview);
    },
    [
      containerRef,
      currentDate,
      minHour,
      postColumnWidth,
      posts.length,
      scrollContainerRef,
      getPlacementIssue,
      workingHours,
    ],
  );

  const stopAutoScroll = useCallback(() => {
    if (autoScrollFrameRef.current !== null) {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
  }, []);

  const runAutoScrollLoop = useCallback(() => {
    stopAutoScroll();

    const tick = () => {
      const session = sessionRef.current;
      const scrollContainer = scrollContainerRef.current;
      if (!session || !didDragRef.current || !scrollContainer) {
        autoScrollFrameRef.current = null;
        return;
      }

      const { x, y } = lastPointerRef.current;
      const shouldContinue = applyAutoScroll(scrollContainer, x, y);
      updatePreviewFromPointer(x, y);

      if (shouldContinue) {
        autoScrollFrameRef.current = requestAnimationFrame(tick);
      } else {
        autoScrollFrameRef.current = null;
      }
    };

    autoScrollFrameRef.current = requestAnimationFrame(tick);
  }, [scrollContainerRef, stopAutoScroll, updatePreviewFromPointer]);

  const stopDrag = useCallback(async () => {
    stopAutoScroll();

    const session = sessionRef.current;
    const currentPreview = previewRef.current;
    const wasDrag = didDragRef.current;
    sessionRef.current = null;
    didDragRef.current = false;

    if (!session) {
      setPreview(null);
      previewRef.current = null;
      return;
    }

    if (!wasDrag) {
      setPreview(null);
      previewRef.current = null;
      return;
    }

    suppressClickRef.current = true;

    if (!currentPreview || !currentDate) {
      setPreview(null);
      previewRef.current = null;
      return;
    }

    if (currentPreview.isInvalid) {
      const issue = getPlacementIssue(
        session.uuid,
        currentPreview.postIndex,
        currentPreview.startMinutes,
        currentPreview.endMinutes,
        session.mode,
        session.resizeEdge,
      );
      if (issue) {
        onInvalidDrop?.(INVALID_PLACEMENT_MESSAGES[issue]);
      }
      setPreview(null);
      previewRef.current = null;
      return;
    }

    const startDate = setMinutesFromMidnight(currentDate, currentPreview.startMinutes);
    const endDate = setMinutesFromMidnight(currentDate, currentPreview.endMinutes);
    const postUuid = posts[currentPreview.postIndex]?.uuid;

    const unchanged =
      currentPreview.startMinutes === session.originStartMinutes &&
      currentPreview.endMinutes === session.originEndMinutes &&
      currentPreview.postIndex === session.originPostIndex;

    if (unchanged) {
      setPreview(null);
      previewRef.current = null;
      return;
    }

    await onReschedule({
      uuid: session.uuid,
      start_time: startDate.toISOString(),
      end_time: endDate.toISOString(),
      post_uuid: postUuid,
    });

    setPreview(null);
    previewRef.current = null;
  }, [currentDate, getPlacementIssue, onInvalidDrop, onReschedule, posts, stopAutoScroll]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      if (!sessionRef.current || event.pointerId !== sessionRef.current.pointerId) return;

      if (didDragRef.current) {
        event.preventDefault();
      }

      applyAutoScroll(scrollContainerRef.current, event.clientX, event.clientY);
      updatePreviewFromPointer(event.clientX, event.clientY);

      if (
        didDragRef.current &&
        autoScrollFrameRef.current === null &&
        scrollContainerRef.current &&
        isNearScrollEdge(scrollContainerRef.current, event.clientX, event.clientY)
      ) {
        runAutoScrollLoop();
      }
    };

    const handleWheel = (event: WheelEvent) => {
      if (!sessionRef.current || !didDragRef.current || !scrollContainerRef.current) return;

      scrollContainerRef.current.scrollBy({
        top: event.deltaY,
        left: event.deltaX,
        behavior: 'auto',
      });
      updatePreviewFromPointer(lastPointerRef.current.x, lastPointerRef.current.y);
    };

    const handlePointerUp = (event: PointerEvent) => {
      if (!sessionRef.current || event.pointerId !== sessionRef.current.pointerId) return;
      void stopDrag();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    window.addEventListener('wheel', handleWheel, { passive: true });

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      window.removeEventListener('wheel', handleWheel);
      stopAutoScroll();
    };
  }, [
    runAutoScrollLoop,
    scrollContainerRef,
    stopAutoScroll,
    stopDrag,
    updatePreviewFromPointer,
  ]);

  const beginDrag = useCallback(
    (
      event: React.PointerEvent,
      booking: AdminBookingResponseDto,
      mode: CalendarDragMode,
      originLeft: number,
      resizeEdge: CalendarResizeEdge = 'end',
    ) => {
      if (!enabled) return;

      const start = new Date(booking.start_time);
      const end = new Date(booking.end_time);
      const { top: originScrollTop, left: originScrollLeft } = getScrollOffsets(
        scrollContainerRef.current,
      );

      didDragRef.current = false;
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      sessionRef.current = {
        mode,
        resizeEdge,
        uuid: booking.uuid,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originLeft,
        originStartMinutes: minutesFromMidnight(start),
        originEndMinutes: minutesFromMidnight(end),
        originPostIndex: getPostIndex(booking),
        originScrollTop,
        originScrollLeft,
      };
    },
    [enabled, getPostIndex, scrollContainerRef],
  );

  const consumeClickIfDragged = useCallback((): boolean => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return true;
    }
    return false;
  }, []);

  return {
    preview,
    beginDrag,
    consumeClickIfDragged,
  };
};
